/**
 * Exclusive market families: at most one pick per match per family.
 * Used by usePickSlips (replace-on-add), AcceptChallengePicksModal (draft), and server validation.
 * Add new families here only; all three consumers use this.
 *
 * Over/Under is split by type: goals full time (1.5/2.5/3.5), goals half time (0.5/1.5/2.5), corners.
 * So you can pick e.g. Goals O/U 2.5 + First Half O/U 1.5 + Corners O/U in the same game.
 * Within each type only one line is allowed (e.g. not both 1.5 and 2.5 goals).
 */

import {
  HANDICAP_MARKET_IDS_ALL,
  FULL_TIME_OVER_UNDER_IDS,
  FIRST_HALF_OVER_UNDER_IDS,
  SECOND_HALF_OVER_UNDER_IDS,
  MARKET_IDS,
} from './matchDetails.constants';

/** Each inner array is a set of market IDs; at most one pick per match per family. */
export const EXCLUSIVE_MARKET_FAMILIES: readonly (readonly number[])[] = [
  [...HANDICAP_MARKET_IDS_ALL],
  [...FULL_TIME_OVER_UNDER_IDS],
  [...FIRST_HALF_OVER_UNDER_IDS],
  [...SECOND_HALF_OVER_UNDER_IDS],
  [MARKET_IDS.CORNERS],
];

const FAMILY_SETS = EXCLUSIVE_MARKET_FAMILIES.map((ids) => new Set(ids));

function getFamilyIndex(marketId: number): number {
  for (let i = 0; i < FAMILY_SETS.length; i++) {
    if (FAMILY_SETS[i].has(marketId)) return i;
  }
  return -1;
}

/** True if marketId belongs to any exclusive family (handicap, over/under, etc.). */
export function isMarketInExclusiveFamily(marketId: number): boolean {
  return getFamilyIndex(marketId) >= 0;
}

/** True if both market IDs belong to the same exclusive family. */
export function isInSameExclusiveFamily(marketIdA: number, marketIdB: number): boolean {
  const a = getFamilyIndex(marketIdA);
  if (a < 0) return false;
  return FAMILY_SETS[a].has(marketIdB);
}
